import React, { useCallback, useState, useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { selectTestCases, updateTestStepAsync, deleteTestStepAsync, updateTestStepMetadataCount } from '../../store/slices/testScenarioSlice'
import { useNotification } from '../../contexts/NotificationContext'
import { InlineEditField } from '../common/InlineEditField'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'
import { CommentItem } from '../common/CommentItem'
import { AddComment } from '../common/AddComment'
import { AttachmentItem } from '../common/AttachmentItem'
import { FileUpload } from '../common/FileUpload'
import {
  getTestStepComments,
  createTestStepComment,
  updateComment,
  deleteComment
} from '../../api/commentsApi'
import {
  getTestStepAttachments,
  uploadTestStepAttachment,
  downloadAttachment,
  deleteAttachment,
  getAttachmentPreviewBlobUrl
} from '../../api/attachmentsApi'
import { getTestStepAuditLog } from '../../api/auditApi'
import { GroupedAuditLogEntries } from '../common/FieldChangeAuditLogItem'
import { prefetchUsers } from '../../utils/userCache'

/**
 * TEST STEP EDIT MODAL
 *
 * Jira-style modal for editing test step details with tabbed interface.
 * Organized into tabs: Details, Comments, Attachments.
 *
 * Features:
 * - Jira-style header with identifier badge and action buttons
 * - Tabbed interface for organized content
 * - Inline edit for stepAction, testData, and expectedResult fields
 * - Each field saves independently to API
 * - Required validation for stepAction field
 * - Comments tab with add/edit/delete
 * - Attachments tab with upload/download/delete
 * - Action bar: Copy link, Delete, Close
 */
export function TestStepEditModal({ isOpen, onClose, testStep }) {
  const dispatch = useDispatch()
  const { showError, showSuccess } = useNotification()

  // Get live test step data from Redux store so edits are reflected immediately
  const testCases = useSelector(selectTestCases)
  const liveTestStep = (() => {
    if (!testStep) return testStep
    const parentCase = testCases.find(tc => tc.id === testStep.testCaseId || tc.id === Number(testStep.testCaseId))
    if (parentCase) {
      const step = parentCase.testSteps?.find(s => s.id === testStep.id || s.id === Number(testStep.id))
      if (step) return { ...step, testCaseId: testStep.testCaseId }
    }
    return testStep
  })()

  // Tab state - 0: Details, 1: Comments, 2: Attachments, 3: Audit Log
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  // State for comments
  const [comments, setComments] = useState([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [showAddComment, setShowAddComment] = useState(false)

  // State for attachments
  const [attachments, setAttachments] = useState([])
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false)

  // State for image preview modal
  const [previewImage, setPreviewImage] = useState(null)
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // State for audit log
  const [auditLog, setAuditLog] = useState([])
  const [isLoadingAuditLog, setIsLoadingAuditLog] = useState(false)

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  // Initial counts from props - shown immediately while API loads
  // These come from the initial scenario data load, so we can display them instantly
  // Note: commentCount may not be available for test steps if not provided by backend
  const initialCommentCount = testStep?.commentCount || 0
  const initialAttachmentCount = testStep?.attachmentCount || 0

  /**
   * Returns initial count while loading, then array length once loaded.
   * This provides instant feedback to users by showing cached counts from
   * the initial data load, then switching to actual array length after API responds.
   */
  const getDisplayCount = (fetchedArray, isLoading, initialCount) => {
    if (isLoading) return initialCount
    return fetchedArray.length
  }

  /**
   * LOAD COMMENTS AND ATTACHMENTS
   * Fetches data when modal opens.
   */
  useEffect(() => {
    if (isOpen && testStep?.id) {
      // Load comments
      setIsLoadingComments(true)
      getTestStepComments(testStep.id)
        .then(data => {
          setComments(data || [])
          // Prefetch user details for all comment authors
          const accountIds = data?.map(c => c.createdBy?.accountId).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load comments:', error)
          setComments([])
        })
        .finally(() => setIsLoadingComments(false))

      // Load attachments
      setIsLoadingAttachments(true)
      getTestStepAttachments(testStep.id)
        .then(data => {
          setAttachments(data || [])
          // Prefetch user details for all uploaders
          const accountIds = data?.map(a => a.createdBy).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load attachments:', error)
          setAttachments([])
        })
        .finally(() => setIsLoadingAttachments(false))

      // Load audit log
      setIsLoadingAuditLog(true)
      getTestStepAuditLog(testStep.id)
        .then(data => {
          setAuditLog(data || [])
          // Prefetch user details for all audit entry authors
          const accountIds = data?.map(e => e.changedByAccountId).filter(Boolean) || []
          if (accountIds.length > 0) {
            prefetchUsers(accountIds)
          }
        })
        .catch(error => {
          console.error('Failed to load audit log:', error)
          setAuditLog([])
        })
        .finally(() => setIsLoadingAuditLog(false))
    }
  }, [isOpen, testStep?.id])

  /**
   * Reset state when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setComments([])
      setAttachments([])
      setAuditLog([])
      setShowAddComment(false)
      setSelectedTabIndex(0)
      setPreviewImage(null)
      // Clean up blob URL to free memory
      if (previewBlobUrl) {
        window.URL.revokeObjectURL(previewBlobUrl)
        setPreviewBlobUrl(null)
      }
    }
  }, [isOpen, previewBlobUrl])

  // ============================================================================
  // ACTION BAR HANDLERS
  // ============================================================================

  /**
   * HANDLE DELETE
   * Opens the delete confirmation modal.
   */
  const handleDelete = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  /**
   * HANDLE CONFIRM DELETE
   * Called when user confirms deletion in the modal.
   * Deletes the test step without showing success notification.
   */
  const handleConfirmDelete = useCallback(async () => {
    try {
      await dispatch(deleteTestStepAsync({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id
      })).unwrap()
      setIsDeleteModalOpen(false)
      onClose()
    } catch (error) {
      console.error('Failed to delete test step:', error)
      const errorMessage = error?.message || 'Failed to delete test step. Please try again.'
      showError('Delete Failed', errorMessage)
    }
  }, [dispatch, testStep, showError, onClose])

  // ============================================================================
  // FIELD HANDLERS
  // ============================================================================

  /**
   * HANDLE STEP ACTION CONFIRM
   * Saves the step action immediately when user confirms edit.
   */
  const handleStepActionConfirm = useCallback(async (newValue) => {
    if (!testStep) return

    try {
      await dispatch(updateTestStepAsync({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        updates: { stepAction: newValue.trim() }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update step action:', error)
      const errorMessage = error?.message || 'Failed to update step action. Please try again.'
      showError('Update Failed', errorMessage)
      throw error
    }
  }, [dispatch, testStep, showError])

  /**
   * HANDLE TEST DATA CONFIRM
   * Saves the test data immediately when user confirms edit.
   */
  const handleTestDataConfirm = useCallback(async (newValue) => {
    if (!testStep) return

    try {
      await dispatch(updateTestStepAsync({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        updates: { testData: newValue.trim() }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update test data:', error)
      const errorMessage = error?.message || 'Failed to update test data. Please try again.'
      showError('Update Failed', errorMessage)
      throw error
    }
  }, [dispatch, testStep, showError])

  /**
   * HANDLE EXPECTED RESULT CONFIRM
   * Saves the expected result immediately when user confirms edit.
   */
  const handleExpectedResultConfirm = useCallback(async (newValue) => {
    if (!testStep) return

    try {
      await dispatch(updateTestStepAsync({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        updates: { expectedResult: newValue.trim() }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update expected result:', error)
      const errorMessage = error?.message || 'Failed to update expected result. Please try again.'
      showError('Update Failed', errorMessage)
      throw error
    }
  }, [dispatch, testStep, showError])

  // ============================================================================
  // COMMENT HANDLERS
  // ============================================================================

  /**
   * HANDLE ADD COMMENT
   */
  const handleAddComment = useCallback(async (content) => {
    if (!testStep) return

    try {
      const newComment = await createTestStepComment(testStep.id, content)
      setComments(prev => [...prev, newComment])
      setShowAddComment(false)
      showSuccess('Comment Added', 'Your comment has been added.')
      // Update Redux metadata count so icon updates immediately
      dispatch(updateTestStepMetadataCount({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        field: 'commentCount',
        delta: 1
      }))
    } catch (error) {
      console.error('Failed to add comment:', error)
      showError('Error', 'Failed to add comment. Please try again.')
      throw error
    }
  }, [testStep, dispatch, showError, showSuccess])

  /**
   * HANDLE EDIT COMMENT
   */
  const handleEditComment = useCallback(async (commentId, content) => {
    try {
      const updatedComment = await updateComment(commentId, content)
      setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c))
      showSuccess('Comment Updated', 'Your comment has been updated.')
    } catch (error) {
      console.error('Failed to edit comment:', error)
      showError('Error', 'Failed to update comment. Please try again.')
      throw error
    }
  }, [showError, showSuccess])

  /**
   * HANDLE DELETE COMMENT
   */
  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await deleteComment(commentId)
      setComments(prev => prev.filter(c => c.id !== commentId))
      showSuccess('Comment Deleted', 'The comment has been deleted.')
      // Update Redux metadata count so icon updates immediately
      dispatch(updateTestStepMetadataCount({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        field: 'commentCount',
        delta: -1
      }))
    } catch (error) {
      console.error('Failed to delete comment:', error)
      showError('Error', 'Failed to delete comment. Please try again.')
      throw error
    }
  }, [testStep, dispatch, showError, showSuccess])

  // ============================================================================
  // ATTACHMENT HANDLERS
  // ============================================================================

  /**
   * HANDLE UPLOAD ATTACHMENT
   */
  const handleUploadAttachment = useCallback(async (file) => {
    if (!testStep) return

    try {
      const newAttachment = await uploadTestStepAttachment(testStep.id, file)
      setAttachments(prev => [...prev, newAttachment])
      showSuccess('File Uploaded', `"${file.name}" has been uploaded.`)
      // Update Redux metadata count so icon updates immediately
      dispatch(updateTestStepMetadataCount({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        field: 'attachmentCount',
        delta: 1
      }))
    } catch (error) {
      console.error('Failed to upload attachment:', error)
      const errorMessage = error.displayMessage || 'Failed to upload file. Please try again.'
      showError('Upload Failed', errorMessage)
      throw error
    }
  }, [testStep, dispatch, showError, showSuccess])

  /**
   * HANDLE DOWNLOAD ATTACHMENT
   */
  const handleDownloadAttachment = useCallback(async (attachment) => {
    try {
      await downloadAttachment(attachment.id, attachment.fileName)
    } catch (error) {
      console.error('Failed to download attachment:', error)
      showError('Download Failed', 'Failed to download file. Please try again.')
    }
  }, [showError])

  /**
   * HANDLE PREVIEW ATTACHMENT
   * Fetches the image as a blob and displays it in the preview modal.
   */
  const handlePreviewAttachment = useCallback(async (attachment) => {
    setPreviewImage(attachment)
    setIsLoadingPreview(true)

    // Clean up previous blob URL if exists
    if (previewBlobUrl) {
      window.URL.revokeObjectURL(previewBlobUrl)
    }

    try {
      const blobUrl = await getAttachmentPreviewBlobUrl(attachment.id)
      setPreviewBlobUrl(blobUrl)
    } catch (error) {
      console.error('Failed to load preview:', error)
      showError('Preview Failed', 'Failed to load image preview.')
      setPreviewImage(null)
    } finally {
      setIsLoadingPreview(false)
    }
  }, [previewBlobUrl, showError])

  /**
   * HANDLE DELETE ATTACHMENT
   */
  const handleDeleteAttachment = useCallback(async (attachmentId) => {
    try {
      await deleteAttachment(attachmentId)
      setAttachments(prev => prev.filter(a => a.id !== attachmentId))
      showSuccess('File Deleted', 'The attachment has been deleted.')
      // Update Redux metadata count so icon updates immediately
      dispatch(updateTestStepMetadataCount({
        testCaseId: testStep.testCaseId,
        testStepId: testStep.id,
        field: 'attachmentCount',
        delta: -1
      }))
    } catch (error) {
      console.error('Failed to delete attachment:', error)
      showError('Error', 'Failed to delete attachment. Please try again.')
      throw error
    }
  }, [testStep, dispatch, showError, showSuccess])

  // ============================================================================
  // TAB CHANGE HANDLER
  // ============================================================================

  /**
   * HANDLE TAB CHANGE
   * Updates selected tab index.
   */
  const handleTabChange = useCallback((index) => {
    setSelectedTabIndex(index)
  }, [])

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
        {/* Backdrop */}
        <div
          className="headless-dialog-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'var(--ds-blanket)',
            zIndex: 1000
          }}
          aria-hidden="true"
        />

        {/* Modal Container - centered with flexbox */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            pointerEvents: 'none'
          }}
        >
          <Dialog.Panel
            style={{
              backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
              borderRadius: '8px',
              boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
              width: '680px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto'
            }}
          >
            {testStep && (
              <>
                {/* JIRA-STYLE HEADER WITH ACTION BAR */}
                <div
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid var(--ds-border, #DFE1E6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                  }}
                >
                  {/* Left: Title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <Dialog.Title
                      style={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: 'var(--ds-text, #172B4D)',
                        margin: 0
                      }}
                    >
                      Edit Test Step
                    </Dialog.Title>
                  </div>

                  {/* Right: Action buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <Tooltip content="Delete">
                      <Button
                        appearance="subtle"
                        iconBefore={<TrashIcon label="Delete" size="medium" />}
                        onClick={handleDelete}
                      />
                    </Tooltip>
                    <Tooltip content="Close">
                      <Button
                        appearance="subtle"
                        iconBefore={<CrossIcon label="Close" size="medium" />}
                        onClick={onClose}
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* TABS - Details | Comments | Attachments | Audit Log */}
                <Tabs
                  id="test-step-edit-tabs"
                  onChange={handleTabChange}
                  selected={selectedTabIndex}
                >
                  {/* Tab list with padding */}
                  <div style={{ padding: '0 24px', borderBottom: '1px solid var(--ds-border, #DFE1E6)' }}>
                    <TabList>
                      <Tab>Details</Tab>
                      <Tab>Comments {getDisplayCount(comments, isLoadingComments, initialCommentCount) > 0 && `(${getDisplayCount(comments, isLoadingComments, initialCommentCount)})`}</Tab>
                      <Tab>Attachments {getDisplayCount(attachments, isLoadingAttachments, initialAttachmentCount) > 0 && `(${getDisplayCount(attachments, isLoadingAttachments, initialAttachmentCount)})`}</Tab>
                      <Tab>Audit Log</Tab>
                    </TabList>
                  </div>

                  {/* DETAILS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {/* Step Action - Required, multiline */}
                      <InlineEditField
                        label="Step Action"
                        value={liveTestStep.title || ''}
                        onConfirm={handleStepActionConfirm}
                        placeholder="Enter step action"
                        multiline
                        rows={3}
                        required
                        requiredMessage="Step action is required"
                      />

                      {/* Test Data - Optional, multiline */}
                      <InlineEditField
                        label="Test Data"
                        value={liveTestStep.testData || ''}
                        onConfirm={handleTestDataConfirm}
                        placeholder="Add test data for this step"
                        multiline
                        rows={3}
                      />

                      {/* Expected Result - Optional, multiline */}
                      <InlineEditField
                        label="Expected Result"
                        value={liveTestStep.expected || ''}
                        onConfirm={handleExpectedResultConfirm}
                        placeholder="Add expected result for this step"
                        multiline
                        rows={3}
                      />
                    </div>
                  </TabPanel>

                  {/* COMMENTS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingComments ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <>
                          {/* Add Comment Button - Always visible at top */}
                          {!showAddComment && (
                            <div style={{ marginBottom: '16px' }}>
                              <Button
                                appearance="subtle"
                                onClick={() => setShowAddComment(true)}
                              >
                                + Add Comment
                              </Button>
                            </div>
                          )}

                          {/* Add Comment Form */}
                          {showAddComment && (
                            <AddComment
                              onSubmit={handleAddComment}
                              onCancel={() => setShowAddComment(false)}
                              placeholder="Add a comment..."
                              autoFocus
                            />
                          )}

                          {/* Comment List */}
                          {comments.length > 0 ? (
                            comments.map(comment => (
                              <CommentItem
                                key={comment.id}
                                comment={comment}
                                onEdit={handleEditComment}
                                onDelete={handleDeleteComment}
                              />
                            ))
                          ) : !showAddComment ? (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: 'var(--ds-text-subtlest, #7A869A)',
                                fontSize: '13px'
                              }}
                            >
                              No comments yet. Click "+ Add Comment" to add one.
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </TabPanel>

                  {/* ATTACHMENTS TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingAttachments ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <>
                          {/* File Upload Zone */}
                          <div id="test-step-file-upload-wrapper">
                            <FileUpload onUpload={handleUploadAttachment} isActive={selectedTabIndex === 2} />
                          </div>

                          {/* Attachment List */}
                          {attachments.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                              {attachments.map(attachment => (
                                <AttachmentItem
                                  key={attachment.id}
                                  attachment={attachment}
                                  onPreview={handlePreviewAttachment}
                                  onDownload={handleDownloadAttachment}
                                  onDelete={handleDeleteAttachment}
                                />
                              ))}
                            </div>
                          )}

                          {/* Empty state */}
                          {attachments.length === 0 && (
                            <div
                              style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: 'var(--ds-text-subtlest, #7A869A)',
                                fontSize: '13px'
                              }}
                            >
                              No attachments yet. Drag and drop files above or click to browse.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </TabPanel>

                  {/* AUDIT LOG TAB PANEL */}
                  <TabPanel>
                    <div
                      style={{
                        padding: '20px 24px',
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(90vh - 180px)'
                      }}
                    >
                      {isLoadingAuditLog ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <Spinner size="small" />
                        </div>
                      ) : auditLog.length > 0 ? (
                        <GroupedAuditLogEntries
                          entries={auditLog}
                          entityType="testStep"
                        />
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: 'var(--ds-text-subtlest, #7A869A)',
                            fontSize: '13px'
                          }}
                        >
                          No activity yet. Changes to this test step will appear here.
                        </div>
                      )}
                    </div>
                  </TabPanel>
                </Tabs>

                {/* Footer - Single Close button */}
                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '1px solid var(--ds-border, #DFE1E6)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexShrink: 0
                  }}
                >
                  <Button
                    appearance="primary"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Test Step"
        itemName={testStep?.title || 'test step'}
        itemType="scenario"
      />

      {/* Image Preview Modal */}
      {previewImage && (
        <Dialog
          open={!!previewImage}
          onClose={() => setPreviewImage(null)}
          className="headless-dialog-root"
        >
          <div
            className="headless-dialog-backdrop"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(9, 30, 66, 0.8)',
              zIndex: 1100
            }}
            aria-hidden="true"
            onClick={() => setPreviewImage(null)}
          />
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1101,
              pointerEvents: 'none',
              padding: '20px'
            }}
          >
            <Dialog.Panel
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                pointerEvents: 'auto',
                position: 'relative'
              }}
            >
              {isLoadingPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
                  <Spinner size="large" />
                </div>
              ) : previewBlobUrl ? (
                <img
                  src={previewBlobUrl}
                  alt={previewImage.fileName}
                  style={{
                    maxWidth: '90vw',
                    maxHeight: '88vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    display: 'block'
                  }}
                />
              ) : null}
              <Button
                appearance="primary"
                onClick={() => setPreviewImage(null)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px'
                }}
              >
                Close
              </Button>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </>
  )
}
